import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { trackEvent } from "@/lib/analytics";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    email: "",
    password: "",
    display_name: "",
    age: 60,
    gender: "female",
    seeking: "male",
    referral_code: params.get("ref") || "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
  }, [params]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.age) < 55) return toast.error("Prime Connect is for adults 55 and over.");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    setLoading(true);
    try {
      await register({ ...form, email: form.email.trim().toLowerCase(), age: Number(form.age) });
      trackEvent("sign_up", { method: "email" });
      toast.success("Welcome to Prime Connect");
      nav("/profile?onboard=1");
    } catch (err) {
      toast.error(errorMessage(err, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.pexels.com/photos/8153967/pexels-photo-8153967.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"
          alt="couple"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/80 via-transparent to-transparent flex items-end p-12">
          <div>
            <h2 className="font-serif-display text-white text-5xl leading-tight">Free to join.</h2>
            <p className="text-white/85 text-lg mt-3">Only pay when someone matches with you.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16 bg-[#faf9f6]">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Create account</span>
          <h1 className="font-serif-display text-4xl md:text-5xl mt-4">Begin your next chapter.</h1>

          <div className="mt-8">
            <GoogleSignInButton label="Sign up with Google" />
            <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#5c5c5c]">
              <span className="h-px flex-1 bg-[#e5e0d8]" />
              <span>or with email</span>
              <span className="h-px flex-1 bg-[#e5e0d8]" />
            </div>
          </div>

          <div className="mt-6 space-y-5">
            <div>
              <label className="text-sm font-medium">Display name</label>
              <input
                value={form.display_name}
                onChange={(e) => set("display_name", e.target.value)}
                required
                className="w-full h-14 mt-2 px-4 rounded-xl border border-[#d6cfc0] focus:border-[#722f37] focus:ring-2 focus:ring-[#722f37]/20 outline-none text-lg bg-white"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required
                className="w-full h-14 mt-2 px-4 rounded-xl border border-[#d6cfc0] focus:border-[#722f37] focus:ring-2 focus:ring-[#722f37]/20 outline-none text-lg bg-white"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password <span className="text-[#5c5c5c] text-xs">(min 8 chars)</span></label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                required
                minLength={8}
                className="w-full h-14 mt-2 px-4 rounded-xl border border-[#d6cfc0] focus:border-[#722f37] focus:ring-2 focus:ring-[#722f37]/20 outline-none text-lg bg-white"
                data-testid="register-password-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium">Age</label>
                <input
                  type="number"
                  min={55}
                  max={110}
                  value={form.age}
                  onChange={(e) => set("age", e.target.value)}
                  required
                  className="w-full h-14 mt-2 px-4 rounded-xl border border-[#d6cfc0] focus:border-[#722f37] focus:ring-2 focus:ring-[#722f37]/20 outline-none text-lg bg-white"
                  data-testid="register-age-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">I am</label>
                <select
                  value={form.gender}
                  onChange={(e) => set("gender", e.target.value)}
                  className="w-full h-14 mt-2 px-3 rounded-xl border border-[#d6cfc0] focus:border-[#722f37] focus:ring-2 focus:ring-[#722f37]/20 outline-none text-lg bg-white"
                  data-testid="register-gender-select"
                >
                  <option value="female">Woman</option>
                  <option value="male">Man</option>
                  <option value="nonbinary">Non-binary</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Seeking</label>
                <select
                  value={form.seeking}
                  onChange={(e) => set("seeking", e.target.value)}
                  className="w-full h-14 mt-2 px-3 rounded-xl border border-[#d6cfc0] focus:border-[#722f37] focus:ring-2 focus:ring-[#722f37]/20 outline-none text-lg bg-white"
                  data-testid="register-seeking-select"
                >
                  <option value="male">Men</option>
                  <option value="female">Women</option>
                  <option value="any">Anyone</option>
                </select>
              </div>
            </div>
          </div>

          <button type="submit" disabled={loading} className="btn-primary mt-8 w-full" data-testid="register-submit-btn">
            {loading ? "Creating account…" : "Create free account"}
          </button>

          <p className="mt-4 text-xs text-[#5c5c5c]">
            By joining you confirm you are 55+ and agree to keep photos tasteful & clothed. Our team moderates uploads.
          </p>

          {form.referral_code && (
            <div className="mt-4 rounded-xl border border-[#c39d2b]/40 bg-[#c39d2b]/10 px-4 py-3 text-sm text-[#8a6a10] flex items-center gap-2" data-testid="referral-applied">
              🎁 Referral code <span className="font-bold uppercase">{form.referral_code}</span> applied — your friend earns a free reveal credit when you rate 3 photos.
            </div>
          )}

          <div className="mt-6 text-sm text-[#5c5c5c]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#722f37] font-medium" data-testid="register-to-login-link">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
